/**
 * Design-token contract for a theme.
 *
 * A "theme" is one visual identity (e.g. Astral Rose) that provides a `dark`
 * and a `light` palette. The engine is deliberately open-ended: adding a new
 * theme is just adding another {@link Theme} object to the registry.
 */

/** The color palette for one color scheme (dark or light). */
export interface ThemeColors {
  /** App background — the deepest layer. */
  background: string;
  /** Slightly raised background (headers, bars). */
  backgroundElevated: string;
  /** Card / panel surface. */
  surface: string;
  /** Raised surface (modals, popovers, pressed cards). */
  surfaceElevated: string;
  /** Hairline borders and dividers. */
  border: string;

  /** Primary brand accent. */
  accent: string;
  /** Accent hover / active state. */
  accentHover: string;
  /** Low-opacity accent for tints and glows. */
  accentSoft: string;
  /** Readable text color on top of the accent. */
  accentContrast: string;

  /** Primary text. */
  textPrimary: string;
  /** Secondary / supporting text. */
  textSecondary: string;
  /** Muted / disabled text. */
  textMuted: string;

  success: string;
  warning: string;
  danger: string;

  /** Scrim behind modals / sheets. */
  overlay: string;
  /** Shadow color used in elevation. */
  shadow: string;

  /** Signature bright/neon glow color for halos & light effects. */
  glow: string;
  /** Translucent accent halo (for soft glows behind elements). */
  accentGlow: string;
  /** Metallic sheen highlight (near-white, subtly tinted). */
  metallic: string;
  /** The brightest reflective stop for metallic text/edges — a pale, almost
   *  white theme tint in dark; a readable lighter accent in light. */
  textHighlight: string;
  /** The darkest stop for metallic text — deep but still legible against the
   *  theme background (never disappears into it). */
  textShade: string;
  /** Translucent color for glowing borders. */
  borderGlow: string;
  /** Subtle top-edge highlight on raised surfaces. */
  surfaceHighlight: string;
  /** Brand gradient — start stop (darkest). */
  gradientStart: string;
  /** Brand gradient — middle stop (the primary). */
  gradientMid: string;
  /** Brand gradient — end stop (lightest). */
  gradientEnd: string;
}

/** Corner radii scale (px). */
export interface ThemeRadii {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  pill: string;
}

export type ColorScheme = 'dark' | 'light';

/** A complete theme: metadata plus a palette per color scheme. */
export interface Theme {
  id: string;
  /** Human-readable name (not localized — themes are proper nouns). */
  name: string;
  radii: ThemeRadii;
  schemes: Record<ColorScheme, ThemeColors>;
}

/** Shared radii used by the built-in themes. */
export const DEFAULT_RADII: ThemeRadii = {
  sm: '10px',
  md: '14px',
  lg: '20px',
  xl: '28px',
  pill: '999px',
};
